/**
 * Utilidades de presentación del FAQ.
 *
 * Los colores de categoría llegan del panel anterior como hexadecimal libre, así
 * que se validan antes de usarse en estilos en línea (Tailwind no puede generar
 * clases dinámicas).
 */

import type { CSSProperties } from 'react';

const HEX = /^#(?:[0-9a-f]{3}|[0-9a-f]{6})$/i;

/** Devuelve el color si es un hexadecimal válido; si no, `null`. */
export function normalizeHex(color: string | null | undefined): string | null {
  const value = (color ?? '').trim();

  return HEX.test(value) ? value : null;
}

/** Fondo tenue + texto con el color de la categoría (chip del icono). */
export function accentSurfaceStyle(color: string | null | undefined): CSSProperties | undefined {
  const hex = normalizeHex(color);

  return hex ? { backgroundColor: `${hex}18`, color: hex } : undefined;
}

/** Punto o barra de acento con el color de la categoría. */
export function accentMarkStyle(color: string | null | undefined): CSSProperties | undefined {
  const hex = normalizeHex(color);

  return hex ? { backgroundColor: hex } : undefined;
}

/** Chip seleccionado: fondo tenue, borde y texto con el color de la categoría. */
export function accentChipStyle(color: string | null | undefined): CSSProperties | undefined {
  const hex = normalizeHex(color);

  return hex ? { backgroundColor: `${hex}14`, borderColor: `${hex}66`, color: hex } : undefined;
}

/** Tamaño de archivo legible (ej. "1,2 MB"). */
export function formatFileSize(bytes: number): string {
  if (!Number.isFinite(bytes) || bytes <= 0) return '—';
  if (bytes < 1024) return `${bytes} B`;

  const units = ['KB', 'MB', 'GB'];
  let size = bytes / 1024;
  let unit = 0;

  while (size >= 1024 && unit < units.length - 1) {
    size /= 1024;
    unit += 1;
  }

  return `${size.toLocaleString('es-PE', { maximumFractionDigits: 1 })} ${units[unit]}`;
}

/** Quita las etiquetas HTML para poder buscar dentro de una respuesta. */
export function stripHtml(html: string | null | undefined): string {
  return String(html ?? '').replace(/<[^>]*>/g, ' ');
}
